import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/data/content";
import { useI18n } from "@/i18n";
import { CraneDivider } from "@/components/CraneMotifs";

export default function Hero() {
  const { t } = useI18n();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="gora"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-ink"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={ASSETS.heroHouse}
          alt={h.alt}
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Mask */}
      <div className="absolute inset-0 hero-mask" />

      {/* Copy */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-72 md:pb-44"
      >
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="serif text-canvas font-light tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[9rem] leading-[0.95]"
        >
          PrzyStań
          <span className="block italic text-canvas/90">
            <span className="text-brand-red font-medium">w</span> Ciszy
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
          className="mt-8 max-w-xl text-canvas/85 text-base md:text-lg leading-relaxed font-light"
        >
          {h.sub[0]}
          <br />
          {h.sub[1]}
          <br />
          {h.sub[2]}
        </motion.p>
      </motion.div>

      {/* Bottom row — przycisk rezerwacji wyrównany z claimem */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, delay: 1 }}
        className="absolute bottom-8 left-0 right-0 z-10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center gap-8 md:grid md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-0">
          <div className="hidden md:block" aria-hidden="true" />

          <div className="text-center md:justify-self-center">
            <span
              aria-label={h.tagline}
              className="block serif uppercase text-lg sm:text-2xl md:text-3xl tracking-normal mb-3 text-canvas md:whitespace-nowrap px-2 leading-tight"
            >
              {h.tagline.split(" ").map((word, i) => (
                <motion.span
                  key={i}
                  aria-hidden="true"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1.2 + i * 0.18 }}
                  className="inline-block mr-[0.3em] last:mr-0"
                >
                  {word}
                </motion.span>
              ))}
            </span>
            <div className="flex flex-col items-center gap-2 text-canvas/80 text-[10px] tracking-[0.4em] uppercase">
              <span>{h.scroll}</span>
              <motion.span
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
                className="w-px h-8 bg-canvas/50 block"
              />
            </div>
          </div>

          <div className="hidden md:block" aria-hidden="true" />
        </div>
      </motion.div>
    </section>
  );
}
